export const getMarkerColorByTime = (updatedOn?: string): string => {
  if (!updatedOn) return "#EF4444";

  const now = new Date();
  const updated = new Date(updatedOn);
  const diffMs = now.getTime() - updated.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 30) {
    return "#10B981";
  } else if (diffMinutes < 60) {
    return "#84CC16";
  } else if (
    updated.getDate() === now.getDate() &&
    updated.getMonth() === now.getMonth() &&
    updated.getFullYear() === now.getFullYear()
  ) {
    return "#F59E0B";
  } else {
    return "#EF4444";
  }
};

export const getTimeStatusLabel = (updatedOn?: string): string => {
  if (!updatedOn) return "No timestamp";

  const now = new Date();
  const updated = new Date(updatedOn);
  const diffMs = now.getTime() - updated.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 30) {
    return "Recently updated (< 30 min)";
  } else if (diffMinutes < 60) {
    return "Updated recently (< 1 hour)";
  } else if (
    updated.getDate() === now.getDate() &&
    updated.getMonth() === now.getMonth() &&
    updated.getFullYear() === now.getFullYear()
  ) {
    return "Updated today";
  } else {
    return "Outdated location";
  }
};

export const formatDistance = (distanceKm: number): string => {
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m`;
  }
  return `${distanceKm.toFixed(1)} km`;
};

// travel time by car [km]
export const estimateTravelTime = (distanceKm: number): string => {
  const avgSpeedKmh = 40; // 40 km/h
  const hours = distanceKm / avgSpeedKmh;
  const totalMinutes = Math.round(hours * 60);

  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  } else {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
};

// distance between two coordinates using Haversine formula
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // earth radius in kilo meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export function createAdvancedMapMarker(
  color: string,
  size: number,
  label?: string,
  isGreeter?: boolean
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "advanced-map-pin";

  // Create the pin container
  const pinContainer = document.createElement("div");
  pinContainer.className = "pin-container";
  pinContainer.style.cssText = `
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    transition: transform 0.3s ease;
  `;

  // Create label (appears above pin)
  if (label) {
    const labelElement = document.createElement("div");
    labelElement.className = "pin-label";
    labelElement.textContent = label;
    labelElement.style.cssText = `
      background: ${color};
      color: white;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;
      white-space: nowrap;
      margin-bottom: 4px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.25);
      border: 2px solid white;
      position: relative;
      z-index: 2;
    `;

    // Add arrow pointing down to pin
    const arrow = document.createElement("div");
    arrow.style.cssText = `
      position: absolute;
      bottom: -6px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid white;
      z-index: 1;
    `;
    labelElement.appendChild(arrow);

    const arrowInner = document.createElement("div");
    arrowInner.style.cssText = `
      position: absolute;
      bottom: 1px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 5px solid ${color};
    `;
    labelElement.appendChild(arrowInner);

    pinContainer.appendChild(labelElement);
  }

  // Create the pin SVG
  const pinSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  pinSvg.setAttribute("width", `${size}`);
  pinSvg.setAttribute("height", `${size * 1.4}`);
  pinSvg.setAttribute("viewBox", "0 0 48 64");
  pinSvg.style.cssText = `
    filter: drop-shadow(0 4px 8px rgba(0,0,0,0.3));
    transition: transform 0.2s ease;
  `;

  const icon = isGreeter ? "👋" : "📍";

  pinSvg.innerHTML = `
    <defs>
      <linearGradient id="pin-grad-${Math.random()}" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
        <stop offset="100%" style="stop-color:${adjustBrightness(
          color,
          -20
        )};stop-opacity:1" />
      </linearGradient>
      <filter id="glow-${Math.random()}">
        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    
    <!-- Outer pin shape -->
    <path 
      d="M24 0c-9.94 0-18 8.06-18 18 0 13.5 15.4 28.1 17.1 29.7.5.5 1.2.5 1.7 0C26.6 46.1 42 31.5 42 18 42 8.06 33.94 0 24 0z" 
      fill="${color}"
      stroke="white" 
      stroke-width="2.5"
    />
    
    <!-- Inner circle for icon/content -->
    <circle cx="24" cy="18" r="9" fill="white" opacity="0.95"/>
    
    <!-- Pulse ring animation -->
    <circle cx="24" cy="18" r="7" fill="none" stroke="${color}" stroke-width="2" opacity="0.6">
      <animate attributeName="r" from="7" to="12" dur="2s" repeatCount="indefinite"/>
      <animate attributeName="opacity" from="0.6" to="0" dur="2s" repeatCount="indefinite"/>
    </circle>
  `;

  // Add emoji/icon overlay
  const iconOverlay = document.createElement("div");
  // iconOverlay.textContent = icon;
  iconOverlay.style.cssText = `
    position: absolute;
    top: ${label ? "calc(100% - " + size * 0.65 + "px)" : size * 0.15 + "px"};
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: ${size * 0.35}px;
    pointer-events: none;
    z-index: 3;
  `;

  pinContainer.appendChild(pinSvg);
  pinContainer.appendChild(iconOverlay);

  // Add hover effect
  pinContainer.addEventListener("mouseenter", () => {
    pinContainer.style.transform = "scale(1.15) translateY(-5px)";
  });

  pinContainer.addEventListener("mouseleave", () => {
    pinContainer.style.transform = "scale(1) translateY(0)";
  });

  wrapper.appendChild(pinContainer);
  return wrapper;
}

// Helper function to adjust color brightness
function adjustBrightness(color: string, percent: number): string {
  const num = parseInt(color.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
}
